/**
 * Competition — a positioning map you can read, or an honest table instead.
 *
 * THIS COMPONENT CHOOSES NOTHING. It does not select axes, derive coordinates,
 * or decide whether a quadrant may be published. All of that is validated
 * server-side against the evidence and arrives as `outputMode`, `axes`, `self`
 * and `entities`. A component that could fall back to plotting whatever it had
 * would defeat the rule it exists to display.
 *
 * TWO WORDS THAT NEVER APPEAR: leader and laggard. A coordinate here is an
 * analytical estimate on a documented axis — not market share, not quality, not
 * a ranking. The quadrant captions are therefore COMPOSED from the two
 * validated axis endpoint labels ("Larger / enterprise · Broad platform") and
 * never from an invented vocabulary of market archetypes, because a corner
 * captioned "Leader" teaches the opposite of the caption underneath it.
 *
 * NAMED NODES, NOT DOTS. The rejected implementation plotted 10px anonymous
 * circles: a reader could not tell which mark was their own company, and the
 * map carried no information the table below did not already carry better. Each
 * node now names its company, distinguishes its class, marks the analysed
 * startup, and opens a focused detail card — which is the only reason to draw a
 * chart rather than print a list.
 *
 * The map is still not load-bearing for accessibility: everything on it is also
 * in the comparison below, which is what a screen reader and a narrow phone get.
 */

import React, { useMemo, useState } from 'react';
import { Text, View } from 'react-native';
import { Star } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SectionDivider } from '@/components/SectionDivider';
import type {
  ClaimView,
  CompetitionBody,
  CompetitorClass,
  NotReassessedReason,
} from '@/lib/strategic-analysis/types';
import { useAnalysisVisuals, useIsWideLayout } from './analysis-visuals';
import { claimFor } from './claim-join';
import {
  AnalysisHero,
  DetailCard,
  FactGrid,
  Footnote,
  InsufficientEvidenceNote,
  LabelledBlock,
  RowDivider,
  SectionCard,
  SectionHeading,
  SourcesLink,
} from './AnalysisPrimitives';
import {
  AXIS_LABELS,
  CLASSIFICATION_EXPLANATIONS,
  CLASSIFICATION_LABELS,
  positionLabel,
} from './framework-labels';

interface CompetitionAnalysisProps {
  body: CompetitionBody;
  claims: ClaimView[];
  /** The analysed company's own name. Absent only where no map is drawn. */
  startupName?: string;
  onInspectClaim?: (claim: ClaimView) => void;
}

/** The selected node: either the analysed startup, or one alternative. */
type Selection = { kind: 'self' } | { kind: 'entity'; entityId: string };

const SELF_LABEL = 'Your startup';

/**
 * Why a check did not complete, in the reader's terms.
 *
 * SANITIZED AND SPECIFIC. The badge used to read "Not checked this run" for all
 * three, and the paragraph above blamed the research budget — which is untrue of
 * a provider outage and hid the only one of the three the founder can actually
 * act on. No provider is named and no error text is shown: the reader needs to
 * know which KIND of limit they hit, not which vendor returned a 503.
 */
const NOT_REASSESSED_BADGES: Record<NotReassessedReason, string> = {
  not_scheduled: 'Not scheduled',
  budget_exhausted: 'Research limit reached',
  provider_failed: 'Search unavailable',
};

const NOT_REASSESSED_REASONS: Record<NotReassessedReason, string> = {
  not_scheduled: 'the check was not scheduled in this run',
  budget_exhausted: 'the research limit was reached before the check completed',
  provider_failed: 'the search provider could not complete the check',
};

export function CompetitionAnalysis({
  body,
  claims,
  startupName,
  onInspectClaim,
}: CompetitionAnalysisProps) {
  const v = useAnalysisVisuals();
  const [x, y] = body.axes;
  const self = body.self ?? null;

  // A MAP IS DRAWN ONLY WHEN THE STARTUP CAN BE PLACED ON IT.
  //
  // `selfPlottable` was computed here, its comment claimed a half-written run
  // would degrade to the table, and `showMap` never consulted it — so a report
  // whose `outputMode` said `quadrant` rendered every competitor with the
  // analysed company silently absent. That is the worst failure this chart has:
  // a founder reading their own position off a map they are not on.
  //
  // Every absence is treated the same way, deliberately. `self: null`,
  // `self: undefined` and a `self` short a coordinate on either published axis
  // all produce the sourced comparison table instead. An "alternatives-only"
  // quadrant was previously tolerated for runs published before the field
  // existed; it is not tolerated now, because the reader cannot tell that
  // variant apart from a complete map and the caption is not what they take
  // away from it.
  //
  // `selectCompetitionAxes` refuses such a pair server-side, so this should be
  // unreachable for a current run. It is checked anyway: the cost of being
  // wrong is a confidently-drawn falsehood, and the cost of the check is a
  // table that carries the same companies.
  const selfPlottable =
    !!self && !!x && !!y &&
    typeof self.positions[x.axis] === 'number' &&
    typeof self.positions[y.axis] === 'number';
  const showMap = body.outputMode === 'quadrant' && body.axes.length === 2 && selfPlottable;

  // The server's own reason, when the server chose the table. When the CLIENT
  // gate is what refused, the server's reason is absent or — worse — describes
  // a different refusal, so this names the real one rather than borrowing a
  // sentence about competitor coverage that was never the problem.
  const refusedForSelf = body.outputMode === 'quadrant' && body.axes.length === 2 &&
    !selfPlottable;

  // COMPARISON IS ONLY CLAIMED WHEN THE SERVER MADE ONE.
  //
  // `comparisonStatus` is the server's own account of whether it could read the
  // previous competition report. `unavailable` means the read failed, and every
  // badge below is suppressed — defaulting to "new" on a failed query would
  // relabel a whole competitor set on the strength of a database error.
  //
  // `hasPriorRun` still requires evidence of an actual comparison, so a report
  // published before these fields existed shows nothing rather than guessing.
  const notReFound = body.notReFound ?? [];
  const notReassessed = body.notReassessed ?? [];
  const comparisonUnavailable = body.comparisonStatus === 'unavailable';
  const hasPriorRun = !comparisonUnavailable &&
    (notReFound.length > 0 || notReassessed.length > 0 ||
      body.entities.some((e) => e.discovery === 'reconfirmed'));
  const mapRefusal = refusedForSelf
    ? 'This company could not be placed on both axes from the evidence, so the comparison below is shown instead of a map that would leave it off.'
    : (body.fallbackReason ??
      'The evidence did not cover enough companies on two comparable axes, so the comparison below is shown instead of a map.');

  const [selection, setSelection] = useState<Selection>(() =>
    selfPlottable ? { kind: 'self' } : { kind: 'entity', entityId: body.entities[0]?.entityId ?? '' }
  );

  const selectedEntity = selection.kind === 'entity'
    ? body.entities.find((e) => e.entityId === selection.entityId) ?? null
    : null;

  const counts = useMemo(() => {
    const by: Record<CompetitorClass, number> = { direct: 0, adjacent: 0, substitute: 0 };
    for (const entity of body.entities) by[entity.classification] += 1;
    return by;
  }, [body.entities]);

  return (
    <View className="gap-4">
      {/* The conclusion, stated only from validated values: which two axes the
          evidence supported, and how the alternatives were classified. There is
          no "positioning signal" field in the model, so no headline is
          manufactured to fill the slot the mockup has for one. */}
      {showMap
        ? (
          <AnalysisHero
            label="Competitive landscape"
            headline={`${AXIS_LABELS[x.axis]} × ${AXIS_LABELS[y.axis]}`}
            body={[x.relevanceRationale, y.relevanceRationale].filter(Boolean).join(' ') || undefined}
            badge={describeMix(counts)}
            accessibilityLabel={`Competitive landscape. Mapped on ${AXIS_LABELS[x.axis]} and ${AXIS_LABELS[y.axis]}. ${describeMix(counts)}.`}
          />
        )
        : (
          <InsufficientEvidenceNote
            title="No positioning map for this analysis"
            detail={mapRefusal}
          />
        )}

      {showMap && (
        <View
          className="rounded-3xl p-3 gap-2"
          style={{
            backgroundColor: v.chart.surface,
            borderWidth: 1,
            borderColor: v.chart.border,
            overflow: 'hidden',
          }}
        >
          <View className="px-1">
            <SectionHeading title="Competitive landscape" note={`${body.entities.length} mapped`} />
            <Text className="mt-0.5 text-[11px]" style={{ color: v.colors.text.tertiary }}>
              Select a company to compare its position
            </Text>
          </View>

          <PositioningMap
            body={body}
            startupName={startupName}
            selfPlottable={selfPlottable}
            selection={selection}
            onSelect={setSelection}
          />

          <Legend />
        </View>
      )}

      {showMap && (
        <SelectedDetail
          body={body}
          claims={claims}
          startupName={startupName}
          selection={selection}
          selectedEntity={selectedEntity}
          onInspectClaim={onInspectClaim}
        />
      )}

      {showMap && (
        <Footnote>
          Positions are analytical estimates against documented endpoints. They are not market
          share, quality, or a ranking.
        </Footnote>
      )}

      {/* NOT SEARCHED IS NOT MISSING. These were named by the previous analysis
          and this run never assessed them — capped out, cut by the research
          budget, or lost to a provider. Merging them into "Not re-found" would
          let a rate-limited provider read as a company that disappeared. */}
      {notReassessed.length > 0 && (
        <View className="gap-2">
          <SectionDivider label="Not reassessed" marginTop={4} marginBottom={4} />
          <View
            className="rounded-2xl p-3 gap-2"
            style={{
              backgroundColor: v.colors.surface.primary,
              borderWidth: 1,
              borderColor: v.colors.border.light,
              overflow: 'hidden',
            }}
            accessibilityRole="summary"
            accessibilityLabel={`${notReassessed.length} alternative(s) from an earlier analysis were not reassessed in this run: ${
              notReassessed.map((e) => `${e.name}, ${NOT_REASSESSED_REASONS[e.reason]}`).join('; ')
            }. Nothing here says they are no longer competitors.`}
          >
            {/* NEUTRAL, because it has to be true of every reason below. The
                previous copy blamed the research budget for all of them, which
                is false for a provider outage. */}
            <Text className="text-[12px] leading-4" style={{ color: v.colors.text.secondary }}>
              An earlier analysis named these and this run did not complete a check on them. Each
              row says why. Nothing here says they are no longer competitors.
            </Text>
            {notReassessed.map((entity) => (
              <View
                key={`not-reassessed-${entity.entityId}`}
                className="flex-row items-center gap-2"
                accessibilityRole="text"
                accessibilityLabel={`${entity.name}: ${NOT_REASSESSED_REASONS[entity.reason]}`}
              >
                <Text
                  numberOfLines={1}
                  className="flex-1 text-[13px]"
                  style={{ color: v.colors.text.primary }}
                >
                  {entity.name}
                </Text>
                <View
                  className="rounded-full px-2 py-1 shrink-0"
                  style={{ backgroundColor: v.colors.surface.secondary }}
                >
                  <Text numberOfLines={1} className="text-[11px]" style={{ color: v.colors.text.tertiary }}>
                    {NOT_REASSESSED_BADGES[entity.reason]}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* The complete equivalent. Present whether or not a map was drawn — on a
          narrow screen and under a screen reader, this IS the analysis. */}
      <View className="gap-2">
        <SectionDivider label="Comparison" marginTop={4} marginBottom={4} />
        {body.entities.map((entity) => (
          <View
            key={entity.entityId}
            accessibilityRole="summary"
            accessibilityLabel={buildEntityLabel(entity, body)}
            className="rounded-2xl p-3 gap-2"
            style={{
              backgroundColor: v.colors.surface.primary,
              borderWidth: 1,
              borderColor: v.colors.border.light,
              overflow: 'hidden',
            }}
          >
            <View className="flex-row items-center gap-2">
              <Text numberOfLines={1} className="flex-1 text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
                {entity.name}
              </Text>
              {/* Only once a previous run exists to be new RELATIVE TO.
                  On a first analysis every company is new, and a badge on all
                  of them says nothing. */}
              {hasPriorRun && entity.discovery === 'new' && (
                <View className="rounded-full px-2 py-1 shrink-0" style={{ backgroundColor: v.colors.primary[50] }}>
                  <Text numberOfLines={1} className="text-[11px] font-semibold" style={{ color: v.colors.primary[700] }}>
                    New
                  </Text>
                </View>
              )}
              <View className="rounded-full px-2 py-1 shrink-0" style={{ backgroundColor: v.colors.surface.secondary }}>
                <Text numberOfLines={1} className="text-[11px]" style={{ color: v.colors.text.secondary }}>
                  {CLASSIFICATION_LABELS[entity.classification]}
                </Text>
              </View>
            </View>

            {body.axes.map((axis) => {
              const at = entity.positions[axis.axis];
              return (
                <View key={`${entity.entityId}-${axis.axis}`} className="flex-row items-start gap-2">
                  <Text numberOfLines={2} className="flex-1 text-[13px]" style={{ color: v.colors.text.secondary }}>
                    {AXIS_LABELS[axis.axis]}
                  </Text>
                  <Text
                    numberOfLines={2}
                    className="text-[13px] text-right shrink-0"
                    style={{ maxWidth: '55%', color: v.colors.text.primary }}
                  >
                    {typeof at === 'number' ? positionLabel(at, axis.lowLabel, axis.highLabel) : 'No evidence'}
                  </Text>
                </View>
              );
            })}

            {/* The table is the ONLY body in comparison-table mode: no map
                means no selected-detail card, and the retained evidence was
                therefore unreachable from the report entirely. A real run
                found this — 14 sources retained and nothing on screen able to
                open them. */}
            <EntitySources entity={entity} claims={claims} onInspectClaim={onInspectClaim} />
          </View>
        ))}
      </View>

      {/* THE COMPARISON ITSELF FAILED. Said plainly, because the alternative is
          a screen that looks like a first analysis and quietly is not. */}
      {comparisonUnavailable && (
        <View className="gap-2">
          <SectionDivider label="Comparison unavailable" marginTop={4} marginBottom={4} />
          <InsufficientEvidenceNote
            title="Previous analysis could not be read"
            detail="The companies below are what this run found. They are not compared against your previous competitive analysis, because it could not be loaded — so nothing here is marked as new or missing."
          />
        </View>
      )}

      {/* A SET THAT SHRANK SAYS SO.
          Discovery is fresh research every run, so companies come and go with
          the search results. Dropping them silently let four competitors vanish
          between refreshes with nothing to tell the reader whether the market
          moved or the top ten results did. These carry no position and no
          classification, because this run found nothing to support one. */}
      {notReFound.length > 0 && (
        <View className="gap-2">
          <SectionDivider label="Not re-found" marginTop={4} marginBottom={4} />
          <View
            className="rounded-2xl p-3 gap-2"
            style={{
              backgroundColor: v.colors.surface.primary,
              borderWidth: 1,
              borderColor: v.colors.border.light,
              overflow: 'hidden',
            }}
            accessibilityRole="summary"
            accessibilityLabel={`${notReFound.length} alternative(s) verified by an earlier analysis were not found by this one: ${
              notReFound.map((e) => e.name).join(', ')
            }.`}
          >
            <Text className="text-[12px] leading-4" style={{ color: v.colors.text.secondary }}>
              An earlier analysis verified these, and this one found no evidence for them. That may
              mean the company changed, or only that this search did not reach it — so they are
              listed rather than dropped, and carry no position here.
            </Text>
            {notReFound.map((entity) => (
              <View key={`not-refound-${entity.entityId}`} className="flex-row items-center gap-2">
                <Text
                  numberOfLines={1}
                  className="flex-1 text-[13px]"
                  style={{ color: v.colors.text.primary }}
                >
                  {entity.name}
                </Text>
                <View
                  className="rounded-full px-2 py-1 shrink-0"
                  style={{ backgroundColor: v.colors.surface.secondary }}
                >
                  <Text numberOfLines={1} className="text-[11px]" style={{ color: v.colors.text.tertiary }}>
                    No evidence this run
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      )}

      <SectionDivider label="How alternatives are classified" marginTop={4} marginBottom={4} />
      <ClassificationKey />
    </View>
  );
}

/** The claim published for one entity, and the way into its sources. */
function EntitySources({
  entity,
  claims,
  onInspectClaim,
}: {
  entity: CompetitionBody['entities'][number];
  claims: ClaimView[];
  onInspectClaim?: (claim: ClaimView) => void;
}) {
  const claim = claimForEntity(entity, claims);
  if (!claim || !onInspectClaim) return null;
  return <SourcesLink count={claim.evidenceIds.length} onPress={() => onInspectClaim(claim)} />;
}

/**
 * The entity's own claim.
 *
 * By claim key first — the server mints `competition.entity.<id>` — and only
 * then by name, because a name match is a guess and an id is not.
 */
function claimForEntity(
  entity: CompetitionBody['entities'][number],
  claims: ClaimView[],
): ClaimView | null {
  // EXACT KEY, WRITTEN BY THE ASSEMBLER. The prose fallback this replaces —
  // `claims.find((c) => c.statement.includes(entity.name))` — is a substring
  // scan: with "Ledger" and "Ledger Systems" in the same market it attaches one
  // company's sources under the other's name, confidently and silently.
  return claimFor(claims, entity.claimKey);
}

/** A factual count of what was classified. Not a judgement about any of them. */
function describeMix(counts: Record<CompetitorClass, number>): string {
  const parts: string[] = [];
  if (counts.direct > 0) parts.push(`${counts.direct} direct`);
  if (counts.adjacent > 0) parts.push(`${counts.adjacent} adjacent`);
  if (counts.substitute > 0) parts.push(`${counts.substitute} substitute`);
  return parts.length > 0 ? parts.join(' · ') : 'No alternatives verified';
}

/** Every axis position and classification, as one sentence for a screen reader. */
function buildEntityLabel(
  entity: CompetitionBody['entities'][number],
  body: CompetitionBody,
): string {
  const positions = body.axes
    .map((axis) => {
      const at = entity.positions[axis.axis];
      const value = typeof at === 'number' ? positionLabel(at, axis.lowLabel, axis.highLabel) : 'no evidence';
      return `${AXIS_LABELS[axis.axis]}: ${value}`;
    })
    .join('. ');
  return `${entity.name}, ${CLASSIFICATION_LABELS[entity.classification]}. ${positions}`;
}

// ---------------------------------------------------------------------------
// The map
// ---------------------------------------------------------------------------

const DOT = 11;
const SELF_DOT = 16;
/** Half a node pill's height, so the mark sits ON its coordinate. */
const NODE_HALF = 13;
/**
 * The plotting area is inset from the plot box, and the inset is not cosmetic.
 *
 * The four quadrant captions live in the top and bottom bands. Without a
 * reserved band a node at position 5 lands exactly where its own caption is
 * printed, and the two render on top of each other — which is what the first
 * rendered pass did.
 */
const INSET_X = 12;
const INSET_Y = 54;

/**
 * A 1..5 rubric position as a fraction of the plot.
 *
 * Five discrete anchored steps, mapped onto a continuous box only for drawing.
 * Nothing between the anchors is meaningful, which is why the axis carries
 * endpoint words rather than a numeric scale.
 */
function fraction(position: number): number {
  return (position - 1) / 4;
}

/** Roughly one pill tall, so a stacked group abuts instead of spreading. */
const STACK_STEP = 26;
/** The startup's node, for co-location purposes. */
export const SELF_NODE_ID = '__self__';

/**
 * Vertical offsets for nodes that share a coordinate.
 *
 * Positions are five anchored steps per axis, so companies with the same
 * evidence legitimately land on the same point — and used to render as one pill
 * exactly on top of another, leaving the covered company invisible and
 * untappable. Founders read that as a missing competitor.
 *
 * PRESENTATION ONLY, and deliberately conservative about it. The stack is
 * CENTRED on the true coordinate, so the group's centre of mass is still the
 * position the evidence supports; the step is about one pill, so the cluster
 * reads as one location rather than as a spread; and the label, the
 * accessibility text and the detail card all state the real position, which is
 * the only place a coordinate is ever asserted. Spacing nodes far enough apart
 * to look like different positions would be inventing precision — that belongs
 * in the methodology, where axis selection now prefers the pair that resolves
 * the pool to the most distinct points.
 */
export function coLocationOffsets(
  nodes: readonly { id: string; x: number; y: number }[],
): Record<string, number> {
  const groups = new Map<string, string[]>();
  for (const node of nodes) {
    const key = `${node.x},${node.y}`;
    const members = groups.get(key) ?? [];
    members.push(node.id);
    groups.set(key, members);
  }

  const offsets: Record<string, number> = {};
  for (const members of groups.values()) {
    for (let i = 0; i < members.length; i++) {
      offsets[members[i]] = (i - (members.length - 1) / 2) * STACK_STEP;
    }
  }
  return offsets;
}

function PositioningMap({
  body,
  startupName,
  selfPlottable,
  selection,
  onSelect,
}: {
  body: CompetitionBody;
  startupName?: string;
  selfPlottable: boolean;
  selection: Selection;
  onSelect: (next: Selection) => void;
}) {
  const v = useAnalysisVisuals();
  const wide = useIsWideLayout();
  const [x, y] = body.axes;
  // Measured rather than assumed: the same component renders at 390 and at
  // 1440, and a fixed square either overflows the narrow one or wastes the wide.
  // Seeded with a plausible width so the first paint draws the chart instead of
  // an empty box that pops in one frame later.
  const [width, setWidth] = useState(wide ? 560 : 342);

  const plotWidth = Math.max(0, width - AXIS_GUTTER);
  const plotHeight = Math.min(Math.max(plotWidth * (wide ? 0.72 : 1.08), 260), wide ? 430 : 360);
  const nodeMax = Math.min(wide ? 190 : 156, Math.max(112, plotWidth * 0.44));

  const plotted = body.entities.filter(
    (e) => typeof e.positions[x.axis] === 'number' && typeof e.positions[y.axis] === 'number',
  );

  // Computed over the plotted set AND the startup together: the startup landing
  // on a competitor is the collision that matters most.
  const offsets = useMemo(
    () =>
      coLocationOffsets([
        ...plotted.map((e) => ({
          id: e.entityId,
          x: e.positions[x.axis],
          y: e.positions[y.axis],
        })),
        ...(selfPlottable && body.self
          ? [
            {
              id: SELF_NODE_ID,
              x: body.self.positions[x.axis],
              y: body.self.positions[y.axis],
            },
          ]
          : []),
      ]),
    [plotted, body.self, selfPlottable, x.axis, y.axis],
  );

  return (
    <View onLayout={(event) => setWidth(event.nativeEvent.layout.width)} className="gap-1">
      <View className="flex-row" style={{ height: plotHeight }}>
        {/* The vertical axis, read bottom-to-top like the axis it labels. */}
        <View style={{ width: AXIS_GUTTER }}>
          <RotatedAxisLabel text={y.highLabel} height={plotHeight / 2} align="flex-end" />
          <RotatedAxisLabel text={y.lowLabel} height={plotHeight / 2} align="flex-start" />
        </View>

        {plotWidth > 0 && (
          <View
            accessibilityRole="image"
            accessibilityLabel={`Positioning map. Horizontal axis ${AXIS_LABELS[x.axis]} from ${x.lowLabel} to ${x.highLabel}. Vertical axis ${AXIS_LABELS[y.axis]} from ${y.lowLabel} to ${y.highLabel}. The same positions are listed in the comparison below.`}
            style={{
              width: plotWidth,
              height: plotHeight,
              borderRadius: 18,
              borderWidth: 1,
              borderColor: v.chart.border,
              backgroundColor: v.colors.surface.secondary,
              overflow: 'hidden',
            }}
          >
            {/* The cross that makes the four zones legible. Not gridlines: the
                rubric has no meaningful subdivisions to grid. */}
            <View
              pointerEvents="none"
              style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, backgroundColor: v.chart.grid }}
            />
            <View
              pointerEvents="none"
              style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, backgroundColor: v.chart.grid }}
            />

            <ZoneLabel text={`${x.lowLabel} · ${y.highLabel}`} corner="nw" />
            <ZoneLabel text={`${x.highLabel} · ${y.highLabel}`} corner="ne" />
            <ZoneLabel text={`${x.lowLabel} · ${y.lowLabel}`} corner="sw" />
            <ZoneLabel text={`${x.highLabel} · ${y.lowLabel}`} corner="se" />

            {plotted.map((entity) => (
              <MapNode
                key={`node-${entity.entityId}`}
                label={entity.name}
                accessibilityLabel={`${entity.name}, ${CLASSIFICATION_LABELS[entity.classification]}, positioned at ${AXIS_LABELS[x.axis]} ${positionLabel(entity.positions[x.axis], x.lowLabel, x.highLabel).toLowerCase()} and ${AXIS_LABELS[y.axis]} ${positionLabel(entity.positions[y.axis], y.lowLabel, y.highLabel).toLowerCase()}`}
                fx={fraction(entity.positions[x.axis])}
                fy={fraction(entity.positions[y.axis])}
                plotWidth={plotWidth}
                plotHeight={plotHeight}
                stackOffset={offsets[entity.entityId] ?? 0}
                maxWidth={nodeMax}
                classification={entity.classification}
                selected={selection.kind === 'entity' && selection.entityId === entity.entityId}
                onPress={() => onSelect({ kind: 'entity', entityId: entity.entityId })}
              />
            ))}

            {/* Last, so the analysed company is never covered by an alternative. */}
            {selfPlottable && body.self && (
              <MapNode
                label={startupName ?? SELF_LABEL}
                accessibilityLabel={`${startupName ?? SELF_LABEL}, your startup, positioned at ${AXIS_LABELS[x.axis]} ${positionLabel(body.self.positions[x.axis], x.lowLabel, x.highLabel).toLowerCase()} and ${AXIS_LABELS[y.axis]} ${positionLabel(body.self.positions[y.axis], y.lowLabel, y.highLabel).toLowerCase()}`}
                fx={fraction(body.self.positions[x.axis])}
                fy={fraction(body.self.positions[y.axis])}
                plotWidth={plotWidth}
                plotHeight={plotHeight}
                stackOffset={offsets[SELF_NODE_ID] ?? 0}
                maxWidth={nodeMax}
                classification="self"
                selected={selection.kind === 'self'}
                onPress={() => onSelect({ kind: 'self' })}
              />
            )}
          </View>
        )}
      </View>

      {/* The horizontal axis, both ends. */}
      <View className="flex-row items-start gap-2" style={{ paddingLeft: AXIS_GUTTER }}>
        <Text numberOfLines={2} className="flex-1 text-[10px] leading-3" style={{ color: v.chart.axisLabel }}>
          ← {x.lowLabel}
        </Text>
        <Text numberOfLines={2} className="flex-1 text-[10px] leading-3 text-right" style={{ color: v.chart.axisLabel }}>
          {x.highLabel} →
        </Text>
      </View>
    </View>
  );
}

const AXIS_GUTTER = 22;

/**
 * A y-axis endpoint, turned to run along the axis it describes.
 *
 * The rotation is about the child's CENTRE, so the child must be centred in a
 * box exactly as tall as the span it should occupy. Anchoring it to the top or
 * bottom of that box instead leaves each label extending half its own length
 * past the box — which is how the two endpoints ended up printed over each
 * other in the middle of the axis.
 */
function RotatedAxisLabel({
  text,
  height,
  align,
}: {
  text: string;
  height: number;
  align: 'flex-start' | 'flex-end';
}) {
  const v = useAnalysisVisuals();
  return (
    <View style={{ height, width: AXIS_GUTTER, justifyContent: 'center', alignItems: 'center' }}>
      <View style={{ width: height, transform: [{ rotate: '-90deg' }] }}>
        <Text
          numberOfLines={1}
          className="text-[10px]"
          style={{ color: v.chart.axisLabel, textAlign: 'center' }}
        >
          {align === 'flex-end' ? `${text} ↑` : `↓ ${text}`}
        </Text>
      </View>
    </View>
  );
}

/**
 * A quadrant caption, composed from the two validated endpoint labels.
 *
 * Never "Leader" / "Challenger" / "Niche" — those name a market standing the
 * evidence does not describe and the rubric cannot support.
 */
function ZoneLabel({ text, corner }: { text: string; corner: 'nw' | 'ne' | 'sw' | 'se' }) {
  const v = useAnalysisVisuals();
  const north = corner === 'nw' || corner === 'ne';
  const west = corner === 'nw' || corner === 'sw';
  return (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        maxWidth: '44%',
        ...(north ? { top: 8 } : { bottom: 8 }),
        ...(west ? { left: 10 } : { right: 10 }),
      }}
    >
      <Text
        numberOfLines={2}
        className="text-[9px] uppercase tracking-wider leading-3"
        style={{ color: v.chart.zoneLabel, textAlign: west ? 'left' : 'right' }}
      >
        {text}
      </Text>
    </View>
  );
}

/**
 * One named company on the map.
 *
 * The DOT sits on the coordinate; the name flows away from the nearer edge, so
 * a node at the far right reads inward instead of off the card. Centring the
 * whole pill on the coordinate — the CSS mockup's `translate(-50%, -50%)` — has
 * no faithful equivalent for a variable-width label in React Native, and
 * clamping it would move the mark off its own position.
 */
function MapNode({
  label,
  accessibilityLabel,
  fx,
  fy,
  plotWidth,
  plotHeight,
  stackOffset,
  maxWidth,
  classification,
  selected,
  onPress,
}: {
  label: string;
  accessibilityLabel: string;
  fx: number;
  fy: number;
  plotWidth: number;
  plotHeight: number;
  /** Vertical nudge that keeps co-located nodes individually readable. */
  stackOffset: number;
  maxWidth: number;
  classification: CompetitorClass | 'self';
  selected: boolean;
  onPress: () => void;
}) {
  const v = useAnalysisVisuals();
  const isSelf = classification === 'self';
  const dot = isSelf ? SELF_DOT : DOT;

  const cx = INSET_X + fx * (plotWidth - INSET_X * 2);
  const cy = INSET_Y + fy * (plotHeight - INSET_Y * 2);
  // Clamped, because a tall stack near an edge would otherwise push its outer
  // members out of the plot box and `overflow-hidden` would clip them away —
  // the same disappearance the stacking exists to prevent.
  const bottom = Math.min(
    Math.max(cy - NODE_HALF + stackOffset, 2),
    Math.max(2, plotHeight - STACK_STEP - 2),
  );
  const flip = fx > 0.55;

  const tone = isSelf
    ? v.colors.primary[600]
    : classification === 'direct'
    ? v.colors.amber.main
    : classification === 'adjacent'
    ? v.colors.text.secondary
    : v.colors.text.tertiary;

  return (
    <View
      style={{
        position: 'absolute',
        bottom,
        ...(flip ? { right: plotWidth - cx - dot / 2 - 4 } : { left: cx - dot / 2 - 4 }),
        maxWidth: Math.max(dot + 12, Math.min(maxWidth, flip ? cx + 6 : plotWidth - cx + 6)),
        zIndex: isSelf ? 4 : selected ? 3 : 2,
        borderRadius: 999,
        borderWidth: selected || isSelf ? 1.5 : 1,
        borderColor: selected ? v.colors.primary[600] : isSelf ? v.colors.primary[600] : v.plate.border,
        backgroundColor: isSelf ? v.colors.primary[50] : v.plate.background,
        ...v.plate.shadow,
      }}
    >
      <AnimatedPressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={accessibilityLabel}
        // The pill is deliberately small so seven of them fit a phone-width
        // plot; the touch target is grown to 44pt with hit slop instead of by
        // inflating the mark.
        hitSlop={{ top: 12, bottom: 12, left: 10, right: 10 }}
        className={`flex-row items-center gap-1.5 ${flip ? 'flex-row-reverse pl-2 pr-1' : 'pl-1 pr-2'}`}
        style={{ paddingVertical: 4 }}
      >
        <View
          className="items-center justify-center shrink-0"
          style={{
            width: dot,
            height: dot,
            borderRadius: classification === 'adjacent' ? 3 : 999,
            backgroundColor: tone,
          }}
        >
          {isSelf && <Star size={9} color={v.colors.text.inverse} fill={v.colors.text.inverse} />}
        </View>
        <Text
          numberOfLines={1}
          className="text-[10px] font-semibold"
          style={{ flexShrink: 1, color: isSelf ? v.colors.primary[900] : v.colors.text.primary }}
        >
          {label}
        </Text>
      </AnimatedPressable>
    </View>
  );
}

function Legend() {
  const v = useAnalysisVisuals();
  const items: { label: string; color: string; square?: boolean; star?: boolean }[] = [
    { label: SELF_LABEL, color: v.colors.primary[600], star: true },
    { label: 'Direct', color: v.colors.amber.main },
    { label: 'Adjacent', color: v.colors.text.secondary, square: true },
    { label: 'Substitute', color: v.colors.text.tertiary },
  ];
  return (
    <View className="flex-row flex-wrap items-center justify-center gap-x-3 gap-y-1 pt-1">
      {items.map((item) => (
        <View key={`legend-${item.label}`} className="flex-row items-center gap-1.5">
          <View
            className="items-center justify-center"
            style={{
              width: 9,
              height: 9,
              borderRadius: item.square ? 2 : 999,
              backgroundColor: item.color,
            }}
          />
          <Text numberOfLines={1} className="text-[10px]" style={{ color: v.colors.text.tertiary }}>
            {item.label}
          </Text>
        </View>
      ))}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Selected detail
// ---------------------------------------------------------------------------

function SelectedDetail({
  body,
  claims,
  startupName,
  selection,
  selectedEntity,
  onInspectClaim,
}: {
  body: CompetitionBody;
  claims: ClaimView[];
  startupName?: string;
  selection: Selection;
  selectedEntity: CompetitionBody['entities'][number] | null;
  onInspectClaim?: (claim: ClaimView) => void;
}) {
  const v = useAnalysisVisuals();
  const isSelf = selection.kind === 'self';
  const name = isSelf ? (startupName ?? SELF_LABEL) : selectedEntity?.name;
  if (!name) return null;

  const positions = isSelf ? body.self?.positions ?? {} : selectedEntity?.positions ?? {};
  const facts = body.axes
    .map((axis) => {
      const at = positions[axis.axis];
      return {
        label: AXIS_LABELS[axis.axis],
        value: typeof at === 'number' ? positionLabel(at, axis.lowLabel, axis.highLabel) : 'No evidence',
      };
    });

  // The evidenced statement the validator published for this entity. Never
  // prose written here to fill the mockup's description slot.
  const claim = selectedEntity ? claimForEntity(selectedEntity, claims) : null;

  return (
    <DetailCard
      title={name}
      badge={isSelf ? SELF_LABEL : selectedEntity ? CLASSIFICATION_LABELS[selectedEntity.classification] : undefined}
      badgeTone={isSelf ? { fore: v.colors.primary[800], back: v.colors.primary[50] } : undefined}
      accessibilityLabel={`Selected: ${name}`}
    >
      <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
        {isSelf
          ? 'Your own coordinates were placed against the same documented anchors as every alternative on this map.'
          : selectedEntity
          ? CLASSIFICATION_EXPLANATIONS[selectedEntity.classification]
          : ''}
      </Text>

      <FactGrid items={facts} />

      {claim && (
        <LabelledBlock label="Supporting evidence">{claim.statement}</LabelledBlock>
      )}

      {claim && (
        <SourcesLink count={claim.evidenceIds.length} onPress={onInspectClaim ? () => onInspectClaim(claim) : undefined} />
      )}
    </DetailCard>
  );
}

function ClassificationKey() {
  const v = useAnalysisVisuals();
  return (
    <SectionCard>
      {(['direct', 'adjacent', 'substitute'] as const).map((classification, index) => (
        <View key={classification} className="gap-1">
          {index > 0 && <RowDivider />}
          <View className={index > 0 ? 'pt-3 gap-1' : 'gap-1'}>
            <View className="flex-row items-center gap-2">
              <View
                className="rounded-full shrink-0"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: classification === 'adjacent' ? 2 : 999,
                  backgroundColor: classification === 'direct'
                    ? v.colors.amber.main
                    : classification === 'adjacent'
                    ? v.colors.text.secondary
                    : v.colors.text.tertiary,
                }}
              />
              <Text numberOfLines={1} className="flex-1 text-[13px] font-semibold" style={{ color: v.colors.text.primary }}>
                {CLASSIFICATION_LABELS[classification]}
              </Text>
            </View>
            {/* Indented under its own marker, so the definition reads as
                belonging to the label rather than as a second column. */}
            <Text className="text-[13px] leading-5" style={{ color: v.colors.text.secondary, paddingLeft: 16 }}>
              {CLASSIFICATION_EXPLANATIONS[classification]}
            </Text>
          </View>
        </View>
      ))}
    </SectionCard>
  );
}
